import { CheckCircle2, XCircle } from "lucide-react";
import StageShell from "@/components/StageShell";
import RulesCard from "@/components/ats/RulesCard";
import PerJdCard from "@/components/ats/PerJdCard";
import KeywordFixCard from "@/components/ats/KeywordFixCard";
import { useRun } from "@/hooks/useRun";

const Pass = ({ ok }) => (ok ? <CheckCircle2 size={15} className="text-emerald-400" /> : <XCircle size={15} className="text-rose-400" />);

function ParseTable({ title, rows }) {
  return (
    <div className="card-surface" data-testid={`parse-test-${title.toLowerCase()}`}>
      <div className="flex items-center mb-2"><div className="font-semibold">{title} read-back</div>
        <span className="ml-auto text-xs font-mono">{rows.filter((r) => r.pass).length}/{rows.length} pass</span></div>
      <div className="space-y-1 max-h-80 overflow-auto scroll-thin">
        {rows.map((r, i) => <div key={i} className="flex items-center gap-2 text-sm"><Pass ok={r.pass} /><span className={r.pass ? "text-slate-300" : "text-rose-300"}>{r.check}</span></div>)}
      </div>
    </div>
  );
}

export default function Stage7({ job, refresh }) {
  const runHook = useRun(job.id, 7, () => refresh());
  const t = job.tests;
  const ok = t && (t.achievable_pct ?? t.match_pct) >= t.match_target;
  return (
    <StageShell job={job} stage={7} refresh={refresh} runHook={runHook}
      runs={[{ action: "ats_tests", label: t ? "Re-run tests" : "Run tests", testid: "ats-tests-run-button" },
        ...(t && (!ok || job.keyword_fix) && t.true_missing?.length ? [{ action: "keyword_fix", label: "Add missing keywords", testid: "keyword-fix-button" }] : [])]}>
      {!t ? <div className="card-surface text-sm text-muted-foreground">Run the Boolean, parse and match tests on the current draft.</div> : (
        <>
          <KeywordFixCard t={t} fix={job.keyword_fix} ok={ok} />
          <div className="grid grid-cols-3 gap-4">
            <div className="card-surface" data-testid="match-score">
              <div className="label-xs">Match on skills the client has</div>
              <div className={`text-5xl font-extrabold font-display mt-1 ${ok ? "text-emerald-400" : "text-amber-400"}`} data-testid="achievable-match">{t.achievable_pct ?? t.match_pct}%</div>
              <div className="text-xs text-muted-foreground">Tier 1 x3 + Tier 2 x2 · target {t.match_target}% · vs all JD keywords: {t.match_pct}%</div>
              {t.lacks?.length > 0 && <div className="text-xs text-slate-400 mt-1">Client doesn't have (not added, see skills-to-build): {t.lacks.join(", ")}</div>}
              {t.tiers && <div className="mt-2 text-xs text-slate-400 space-y-0.5" data-testid="tier-breakdown">{["1", "2", "3"].map((k) => <div key={k}>Tier {k}: {t.tiers[k].covered}/{t.tiers[k].total}</div>)}</div>}
              {t.missing.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{t.missing.map((m) => <span key={m} className="text-[11px] px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-300">{m}</span>)}</div>}
            </div>
            <div className="card-surface col-span-2" data-testid="boolean-results">
              <div className="label-xs mb-2">Recruiter Boolean searches (evaluated by code)</div>
              {t.booleans.map((b, i) => (
                <div key={i} className="py-2 border-t border-border first:border-0" data-testid={`boolean-row-${i}`}>
                  <div className="flex items-center gap-2"><Pass ok={b.pass} /><code className="text-xs text-slate-200 break-all">{b.query}</code>
                    <span className={`ml-auto text-xs font-semibold ${b.pass ? "text-emerald-300" : "text-rose-300"}`}>{b.pass ? "PASS" : "FAIL"}</span></div>
                  <div className="flex flex-wrap gap-1 mt-1 ml-6">{Object.entries(b.terms).map(([k, v]) => <span key={k} className={`text-[10px] px-1.5 rounded ${v ? "bg-emerald-500/10 text-emerald-300" : "bg-slate-800 text-slate-500 line-through"}`}>{k}</span>)}</div>
                  {b.error && <div className="text-xs text-amber-300 ml-6">Could not parse: {b.error}</div>}
                </div>
              ))}
            </div>
          </div>
          <PerJdCard pj={t.per_jd} />
          <RulesCard rules={t.lint} />
          <div className="grid grid-cols-2 gap-4">
            <ParseTable title="DOCX" rows={t.parse.docx} />
            <ParseTable title="PDF" rows={t.parse.pdf} />
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2"><Pass ok={t.parse.page_header_empty} /> Contact details are in the body, not the page header</div>
        </>
      )}
    </StageShell>
  );
}
